/** @jsxImportSource @emotion/react */
import { CircularProgress, Grid } from '@mui/material';
import React, { useMemo } from 'react';
import dayjs from 'dayjs';
import { useTelegram } from '../../../hooks/useTelegram';
import { meetingRoomBookingApi } from '../../../api/meetingRoomBookingApi';
import { MEETING_ROOM_BOOKING_LIST_INCLUDE } from '../../../api/types';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { REPEAT_VALUE, USER_TEAM } from '../../../constants';
import { HeaderText, getRoomShortName } from './styles';
import MeetingRoomTable from './MeetingRoomTable/MeetingRoomTable';
import { MeetingRoomBooking } from '../../../types';
import { CellBooking, MeetingRoomConfig } from '../../../types/meetingRoomTable';

export const MEETING_ROOMS: MeetingRoomConfig[] = [
    {
        id: 'MR1',
        name: 'MR 1',
        isVisible: () => true,
    },
    // MR2 temporarily hidden
    // {
    //   id: 'MR2',
    //   name: 'MR 2',
    //   isVisible: () => false
    // },
    {
        id: 'MR3',
        name: 'MR 3',
        isVisible: (team) => team?.includes(USER_TEAM.ADMINISTRATIVE) || false,
    },
];

type MeetingRoomLayoutProps = {
    date: dayjs.Dayjs;
    onCellClick: (startTime: string, endTime: string, meetingRoomName: string) => void;
    editable?: boolean;
    onEditClick?: (
        booking: MeetingRoomBooking,
        meetingRoomName?: string,
        telegramUserId?: number,
    ) => void;
    onDeleteClick?: (bookingId: string) => void;
};

export const MeetingRoomLayout: React.FC<MeetingRoomLayoutProps> = ({
    date,
    onCellClick,
    editable,
    onEditClick,
    onDeleteClick,
}) => {
    const { tgUserId, colorScheme } = useTelegram();
    const currentUser = useAppSelector((store) => store.userReducer.user);

    const formattedDate = useMemo(() => date.format('YYYY-MM-DD'), [date]);

    const { isLoading, data: bookingsResponse } = meetingRoomBookingApi.useListQuery({
        filters: { date: formattedDate },
        offset: 0,
        include: [
            MEETING_ROOM_BOOKING_LIST_INCLUDE.USERS,
            MEETING_ROOM_BOOKING_LIST_INCLUDE.MEETING_ROOMS,
        ],
        includeCount: true,
    });

    const rooms = useMemo(
        () => MEETING_ROOMS.filter((room) => room.isVisible(currentUser.team)),
        [currentUser],
    );

    // Organize bookings by room - transform to minimal cell data
    const roomBookings: Record<string, CellBooking[]> = useMemo(() => {
        if (!bookingsResponse) return {};

        const bookings: Record<string, CellBooking[]> = {};

        rooms.forEach((room) => {
            bookings[room.id] = bookingsResponse.data
                .filter((b) => getRoomShortName(b.meetingRoom?.name) === room.id)
                .map((booking) => ({
                    ...booking,
                    repeatValue: booking.repeatValue as REPEAT_VALUE,
                    userData: {
                        telegramUserId: booking.user!.telegramUserId,
                        name: booking.user!.name,
                        surname: booking.user!.surname,
                    },
                }));
        });
        return bookings;
    }, [bookingsResponse, rooms]);

    const currentUserBookings: CellBooking[] = useMemo(() => {
        if (!bookingsResponse) return [];

        return bookingsResponse.data
            .filter((b) => Number(b.user?.telegramUserId) === tgUserId)
            .map((booking) => ({
                ...booking,
                repeatValue: booking.repeatValue as REPEAT_VALUE,
                userData: {
                    telegramUserId: booking.user!.telegramUserId,
                    name: booking.user!.name,
                    surname: booking.user!.surname,
                },
            }));
    }, [bookingsResponse, tgUserId]);

    if (isLoading) return <CircularProgress size={40} />;

    return (
        <div>
            {/* Header with room names */}
            <Grid container justifyContent="center" mb="20px" overflow="auto">
                <Grid
                    item
                    xs={12}
                    display="flex"
                    mt="20px"
                    ml="50px"
                    justifyContent={rooms.length > 1 ? 'flex-start' : 'center'}
                >
                    {rooms.map((room) => (
                        <Grid
                            key={room.id}
                            item
                            xs={rooms.length > 1 ? 6 : 12}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            flexDirection="column"
                        >
                            <HeaderText colorScheme={colorScheme}>{room.name}</HeaderText>
                        </Grid>
                    ))}
                </Grid>
            </Grid>

            <MeetingRoomTable
                rooms={rooms}
                roomBookings={roomBookings}
                currentUserBookings={currentUserBookings}
                formattedDate={formattedDate}
                onCellClick={onCellClick}
                editable={editable}
                onEditClick={onEditClick}
                onDeleteClick={onDeleteClick}
            />
        </div>
    );
};

export default MeetingRoomLayout;
